import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  SONAR_V3_ALPHA_CREATE_SESSION,
  SONAR_V3_CREATE_SESSION,
  SONAR_V3_POLL,
  SONAR_V3_POLL_ALPHA,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { ErrorValues } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/itineraries/';
import { isSonarV3ErrorPayload } from '@web-platform/shared-universal-utils-frontend/src/helpers/isSonarErrorPayload';
import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { LOCATION_CHANGED } from '../../../../../../actions/location';
import { isEmptyItinerary, isNullishItinerary, isSonarV3ItineraryNotFound } from '../utils';

import { mapSonarV3Itineraries } from './sonarV3ItinerariesMapper';

import type {
  SonarV3PayloadResponse,
  SonarV3PayloadResponseSuccess,
} from '@web-platform/shared-universal-types/src/network/types';
import type {
  LocationChangedAction,
  MutationResponseAction,
  QueryResponseAction,
  SonarV3AlphaPayloadResponseSuccess,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  ErrorType,
  ItineraryBookingPanel,
  ItineraryByIdBookingPanel,
  ItineraryStateBookingPanel,
  PricingItemBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';
import type { ActionType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

export const INITIAL_STATE: ItineraryStateBookingPanel = {
  byId: {},
};

const getOldPricingItems = (state: ItineraryByIdBookingPanel, itineraryId: string) =>
  state[itineraryId].pricing_options.flatMap((option) => option.items || []);

const mapPricingItemWithOldItem = (
  item: PricingItemBookingPanel,
  oldPricingItems: PricingItemBookingPanel[],
) => {
  const oldItem = oldPricingItems.find((i) => i.agent_id === item.agent_id);
  if (oldItem?.farePolicies) {
    return { ...item, farePolicies: oldItem.farePolicies };
  }
  return item;
};

const withFarePolicyForSonar =
  (state: ItineraryByIdBookingPanel, itineraryIdentifier: string) =>
  (itinerary: ItineraryBookingPanel): ItineraryBookingPanel => {
    if (!state?.[itineraryIdentifier]) {
      return itinerary;
    }
    const oldPricingItems = getOldPricingItems(state, itineraryIdentifier);
    if (!oldPricingItems.some((i) => i.farePolicies)) {
      return itinerary;
    }
    return {
      ...itinerary,
      pricing_options: itinerary.pricing_options.map((option) => ({
        ...option,
        items: option.items?.map((item) => mapPricingItemWithOldItem(item, oldPricingItems)),
      })),
    };
  };

const buildErroredItinerary = (
  error: ErrorType,
  id: string,
  token?: string,
): ItineraryBookingPanel => ({
  id,
  token,
  leg_ids: [],
  pricing_options: [],
  score: 0,
  error,
});

const getSonarErrorTypeForItinerary = (response: SonarV3PayloadResponse): ErrorType => {
  if (isSonarV3ErrorPayload(response)) {
    if (response.status === 504) {
      return ErrorValues.TIMEOUT;
    }
    if (isSonarV3ItineraryNotFound(response)) {
      return ErrorValues.UNAVAILABLE;
    }
    return ErrorValues.GENERAL;
  }
  return ErrorValues.NONE;
};

const findStateKey = (
  state: ItineraryByIdBookingPanel,
  identifier: string,
  isToken: boolean,
): string => {
  if (identifier in state) {
    return identifier;
  }
  const matchKey = isToken ? 'token' : 'id';
  const foundEntry = Object.entries(state).find(
    ([_, itinerary]) => itinerary[matchKey] === identifier,
  );
  return foundEntry?.[0] ?? identifier;
};

const resolveStateKey = (
  state: ItineraryByIdBookingPanel,
  itineraryId: unknown,
  itineraryToken: unknown,
  isTokenPanel: boolean,
): string => {
  if (isTokenPanel && typeof itineraryToken === 'string') {
    return findStateKey(state, itineraryToken, true);
  }
  if (!isTokenPanel && typeof itineraryId === 'string') {
    return findStateKey(state, itineraryId, false);
  }
  return '';
};

const handleErrorResponse = (
  state: ItineraryByIdBookingPanel,
  response: SonarV3PayloadResponse,
  isPolling: boolean,
  itineraryTokenOrId: string,
  itineraryId: unknown,
  itineraryToken: unknown,
): ItineraryByIdBookingPanel => {
  const error = getSonarErrorTypeForItinerary(response);

  if (
    isPolling &&
    state[itineraryTokenOrId]?.error &&
    state[itineraryTokenOrId]?.error !== ErrorValues.NONE
  ) {
    return state;
  }

  const isValidItineraryInState =
    itineraryTokenOrId in state &&
    (!state[itineraryTokenOrId].error || state[itineraryTokenOrId].error === ErrorValues.NONE);

  if (isPolling && error !== ErrorValues.UNAVAILABLE && isValidItineraryInState) {
    return state;
  }

  return {
    ...state,
    [itineraryTokenOrId]: buildErroredItinerary(
      error,
      typeof itineraryId === 'string' ? itineraryId : '',
      typeof itineraryToken === 'string' ? itineraryToken : undefined,
    ),
  };
};

const handleEmptyOrNullResponse = (
  state: ItineraryByIdBookingPanel,
  response: SonarV3PayloadResponseSuccess | SonarV3AlphaPayloadResponseSuccess,
  itineraryTokenOrId: string,
  itineraryId: unknown,
  itineraryToken: unknown,
): ItineraryByIdBookingPanel => {
  if (!response.body?.pollingCompleted) {
    return state;
  }
  return {
    ...state,
    [itineraryTokenOrId]: buildErroredItinerary(
      ErrorValues.UNAVAILABLE,
      typeof itineraryId === 'string' ? itineraryId : '',
      typeof itineraryToken === 'string' ? itineraryToken : undefined,
    ),
  };
};

const handleSuccessResponse = (
  state: ItineraryByIdBookingPanel,
  response: SonarV3PayloadResponseSuccess | SonarV3AlphaPayloadResponseSuccess,
  itineraryTokenOrId: string,
  isTokenPanel: boolean,
): ItineraryByIdBookingPanel => {
  const newState = mapSonarV3Itineraries(state, response, isTokenPanel);

  const responseItineraryId = response.body?.itinerary?.id;
  const responseItineraryToken = response.body?.itinerary?.token;

  const key = isTokenPanel ? responseItineraryToken || responseItineraryId : responseItineraryId;

  if (!key) {
    return newState;
  }

  const itineraryWithFarePolicy = withFarePolicyForSonar(state, itineraryTokenOrId)(newState[key]);

  if (itineraryWithFarePolicy === newState[key]) {
    return newState;
  }

  return {
    ...newState,
    [key]: itineraryWithFarePolicy,
  };
};

const byId = (
  state: ItineraryByIdBookingPanel,
  action: MutationResponseAction | QueryResponseAction,
  isTokenPanel: boolean,
): ItineraryByIdBookingPanel => {
  switch (action.payload.type) {
    case SONAR_V3_ALPHA_CREATE_SESSION:
    case SONAR_V3_CREATE_SESSION:
    case SONAR_V3_POLL_ALPHA:
    case SONAR_V3_POLL: {
      const { itineraryId, itineraryToken } = action.payload.variables;

      const isTokenResponse = typeof itineraryToken === 'string';

      if (isTokenPanel && !isTokenResponse) {
        logWarn({
          component: 'itinerariesReducer',
          message:
            'Itinerary token booking panel route used without a valid itineraryToken in action variables',
        });
      }

      const { response } = action.payload;
      const isPolling =
        action.payload.type === SONAR_V3_POLL || action.payload.type === SONAR_V3_POLL_ALPHA;

      const isNonSuccessResponse =
        isSonarV3ErrorPayload(response) ||
        isEmptyItinerary(response) ||
        isNullishItinerary(response);

      if (isNonSuccessResponse && !isTokenResponse && typeof itineraryId !== 'string') {
        logError({
          component: 'itinerariesReducer',
          message: `Unable to handle Sonar V3 response with invalid itineraryId: ${String(itineraryId)}`,
          error: new Error('Invalid itineraryId'),
        });
        return state;
      }

      const itineraryTokenOrId = resolveStateKey(
        state,
        itineraryId,
        itineraryToken,
        isTokenResponse,
      );

      if (isSonarV3ErrorPayload(response)) {
        return handleErrorResponse(
          state,
          response,
          isPolling,
          itineraryTokenOrId,
          itineraryId,
          itineraryToken,
        );
      }

      if (isEmptyItinerary(response) || isNullishItinerary(response)) {
        return handleEmptyOrNullResponse(
          state,
          response,
          itineraryTokenOrId,
          itineraryId,
          itineraryToken,
        );
      }

      return handleSuccessResponse(state, response, itineraryTokenOrId, isTokenResponse);
    }

    default:
      return state;
  }
};

export type GetExitingItineraryId = (
  location: LocationChangedAction['payload']['location'],
  prevLocation: LocationChangedAction['payload']['prevLocation'],
) => string | undefined;

export const createItinerariesReducer = (
  isTokenPanel: () => boolean,
  getExitingBookingPanelItineraryId: GetExitingItineraryId,
) => {
  const handleLocationChange = (
    state: ItineraryStateBookingPanel,
    location: LocationChangedAction['payload']['location'],
    prevLocation: LocationChangedAction['payload']['prevLocation'],
  ): ItineraryStateBookingPanel => {
    const itineraryId = getExitingBookingPanelItineraryId(location, prevLocation);
    if (!itineraryId) return state;

    const itinerary = state.byId[itineraryId];

    if (!itinerary?.error || itinerary.error === ErrorValues.NONE) return state;

    const { [itineraryId]: _removed, ...remainingItineraries } = state.byId;
    return { byId: remainingItineraries };
  };

  return (
    state: ItineraryStateBookingPanel = INITIAL_STATE,
    action: ActionType,
  ): ItineraryStateBookingPanel => {
    switch (action.type) {
      case MUTATION_RESPONSE_RECEIVED:
      case QUERY_RESPONSE_RECEIVED:
        return {
          byId: byId(state.byId, action, isTokenPanel()),
        };

      case SEARCH_PARAMS_CHANGED:
        return INITIAL_STATE;

      case LOCATION_CHANGED: {
        return handleLocationChange(state, action.payload.location, action.payload.prevLocation);
      }

      default:
        return state;
    }
  };
};
